import { useCallback, useEffect, useState } from "react";
import { fetchPortalPassword } from "../../../api/postCall/fetchPortalPassword";
import { decryptPassword } from "../../../utils/SimplePasswordCrypto";
import { CredentialWithId, VisibilityState } from "../types/credentialTypes";

const INITIAL_VISIBILITY_STATE: VisibilityState = {
  isVisible: false,
  value: "",
  isLoading: false,
};

/**
 * Encapsulates the password + secret-key reveal flow for a selected credential.
 * Resets visibility whenever the active credential id changes, mirroring the
 * behavior of the original page-level effect.
 */
export const useCredentialReveal = (
  credentialDetails: CredentialWithId | null,
  selectedCredentialId: string | null,
) => {
  const [passwordState, setPasswordState] = useState<VisibilityState>(
    INITIAL_VISIBILITY_STATE,
  );
  const [secretKeyState, setSecretKeyState] = useState<VisibilityState>(
    INITIAL_VISIBILITY_STATE,
  );

  const resetReveal = useCallback(() => {
    setPasswordState(INITIAL_VISIBILITY_STATE);
    setSecretKeyState(INITIAL_VISIBILITY_STATE);
  }, []);

  useEffect(() => {
    resetReveal();
  }, [selectedCredentialId, resetReveal]);

  const togglePasswordVisibility = useCallback(async () => {
    if (!passwordState.isVisible && credentialDetails?.credential_id) {
      setPasswordState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await fetchPortalPassword(
          credentialDetails.credential_id,
        );
        const decryptedPassword = await decryptPassword(response.secret_value);
        setPasswordState({
          isVisible: true,
          value: decryptedPassword,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching or decrypting password:", error);
        setPasswordState((prev) => ({
          ...prev,
          isVisible: false,
          isLoading: false,
        }));
      }
    } else {
      setPasswordState(INITIAL_VISIBILITY_STATE);
    }
  }, [credentialDetails?.credential_id, passwordState.isVisible]);

  const toggleSecretKeyVisibility = useCallback(async () => {
    if (!secretKeyState.isVisible && credentialDetails?.secret_key) {
      setSecretKeyState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await fetchPortalPassword(
          credentialDetails.secret_key,
        );
        const decryptedSecretKey = await decryptPassword(response.secret_value);
        setSecretKeyState({
          isVisible: true,
          value: decryptedSecretKey,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching or decrypting secret key:", error);
        setSecretKeyState((prev) => ({
          ...prev,
          isVisible: false,
          isLoading: false,
        }));
      }
    } else {
      setSecretKeyState(INITIAL_VISIBILITY_STATE);
    }
  }, [credentialDetails?.secret_key, secretKeyState.isVisible]);

  return {
    passwordState,
    secretKeyState,
    togglePasswordVisibility,
    toggleSecretKeyVisibility,
    resetReveal,
  };
};
